<template>
  <div class="project-timeline">
    <div v-if="loading" class="timeline-loading">Loading timeline…</div>
    <div v-else-if="error" class="timeline-error">{{ error }}</div>
    <div v-else class="timeline-content">
      <h2>{{ data.name }}</h2>
      <div class="timeline-track">
        <div
          v-for="ms in data.milestones"
          :key="ms.milestoneId"
          class="timeline-item"
          :class="statusClass(ms)"
        >
          <div class="timeline-marker"></div>
          <div class="timeline-detail">
            <strong>{{ ms.title }}</strong>
            <span class="timeline-status">{{ ms.status }}</span>
            <span v-if="ms.dueOn" class="timeline-due">Due: {{ ms.dueOn }}</span>
            <span v-if="ms.overdue" class="timeline-overdue">OVERDUE</span>
            <span v-if="ms.signoffStatus" class="timeline-signoff">{{ ms.signoffStatus }}</span>
            <span v-if="ms.paymentReady" class="timeline-payment">Payment ready</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

const props = defineProps<{ url: string }>()

interface Milestone {
  milestoneId: number
  title: string
  position: number
  status: string
  startsOn: string | null
  dueOn: string | null
  overdue: boolean
  signoffStatus: string | null
  paymentReady: boolean
}

interface TimelineData {
  projectId: number
  name: string
  status: string
  startsOn: string | null
  endsOn: string | null
  milestones: Milestone[]
}

const data = ref<TimelineData>({ projectId: 0, name: '', status: '', startsOn: null, endsOn: null, milestones: [] })
const loading = ref(true)
const error = ref<string | null>(null)

function statusClass(ms: Milestone): string {
  const classes = [`status-${ms.status}`]
  if (ms.overdue) classes.push('overdue')
  if (ms.paymentReady) classes.push('payment-ready')
  return classes.join(' ')
}

onMounted(async () => {
  try {
    const response = await fetch(props.url, {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    data.value = await response.json()
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load timeline'
  } finally {
    loading.value = false
  }
})
</script>
